import * as React from "react";
import { useState } from "react";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar,
  Alert,
  TextField,
  InputAdornment,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import DeleteRoundedIcon from "@mui/icons-material/DeleteRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import axios from '../../../Configs/axiosInstance';
import { MAIN_URL } from "../../../Configs/Urls";
import CustomizedDataGrid from "../../Components/CustomizedDataGrid";
import { getStoredPageSize, setStoredPageSize } from "../../../Configs/paginationStorage";

export default function MainGrid() {

  const [DietPlans, setDietPlans] = React.useState([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const [paginationModel, setPaginationModel] = React.useState({
    page: 0,
    pageSize: getStoredPageSize("dietplans"),
  });
  const handlePaginationModelChange = (model) => {
    setPaginationModel(model);
    setStoredPageSize("dietplans", model.pageSize);
  };
  const [searchTerm, setSearchTerm] = useState("");

  const [showAddEditDialog, setShowAddEditDialog] = React.useState(false);
  const [dietPlanId, setDietPlanId] = React.useState(null);
  const [dietPlanValue, setDietPlanValue] = React.useState("");

  const [openDeleteDialog, setOpenDeleteDialog] = React.useState(false);
  const [Deleteloading, setDeleteLoading] = React.useState(false);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [snackbarMessage, setSnackbarMessage] = React.useState("");
  const [snackbarSeverity, setSnackbarSeverity] = React.useState("success");

  // Fetch exactly one page from the backend — either the plain listing or
  // the search endpoint, depending on whether there's a search term. Both
  // return the same { data, meta } shape.
  const fetchDietPlans = async () => {
    try {
      setLoading(true);
      setError(null);

      const trimmedSearch = searchTerm.trim();
      const endpoint = trimmedSearch ? "dietplans/search" : "dietplans";
      const params = {
        page: paginationModel.page + 1, // backend pages are 1-indexed
        per_page: paginationModel.pageSize,
        ...(trimmedSearch ? { query: trimmedSearch } : {}),
      };

      const response = await axios.get(`${MAIN_URL}${endpoint}`, { params });

      setDietPlans(response.data.data);
      setRowCount(response.data.meta.total);
    } catch (error) {
      console.error("Error fetching DietPlans:", error);
      setError("Failed to fetch DietPlans. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Refetch whenever the page/page-size changes, or 450ms after the admin
  // stops typing in the search box.
  React.useEffect(() => {
    const handler = setTimeout(fetchDietPlans, searchTerm ? 450 : 0);
    return () => clearTimeout(handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel, searchTerm]);

  // A new search resets to page 1 — otherwise the admin can land on a page
  // that no longer exists for the narrower result set.
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
  };

  const handleDelete = (id) => {
    setDietPlanId(id);
    setOpenDeleteDialog(true);
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
  };

  const confirmDelete = async () => {
    setDeleteLoading(true);
    try {
      await axios.delete(`${MAIN_URL}dietplans/${dietPlanId}`);
      setSnackbarMessage("Diet plan deleted successfully");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);
      fetchDietPlans(); // re-fetch so pagination/total stays accurate
    } catch (error) {
      console.error("Error deleting DietPlan:", error);
      setSnackbarMessage(
        error?.response?.data?.message
          ? error?.response.data.message
          : "Error deleting diet plan. Please try again."
      );
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    } finally {
      setOpenDeleteDialog(false);
      setDietPlanId(null);
      setDeleteLoading(false);
    }
  };

  const handleOpenAddDialog = () => {
    setDietPlanId(null);
    setDietPlanValue("");
    setShowAddEditDialog(true);
  };

  const handleOpenEditDialog = (dietPlan) => {
    setDietPlanId(dietPlan.id);
    setDietPlanValue(dietPlan.value);
    setShowAddEditDialog(true);
  };

  const handleSaveDietPlan = async () => {
    try {
      const data = { value: dietPlanValue };
      if (dietPlanId) {
        await axios.put(`${MAIN_URL}dietplans/${dietPlanId}`, data);
        setSnackbarMessage("Diet plan updated successfully.");
      } else {
        await axios.post(`${MAIN_URL}dietplans`, data);
        setSnackbarMessage("Diet plan added successfully.");
      }
      setSnackbarSeverity("success");
      fetchDietPlans();
    } catch (error) {
      setSnackbarMessage(
        error?.response?.data?.message || "Failed to save diet plan."
      );
      setSnackbarSeverity("error");
    } finally {
      setShowAddEditDialog(false);
      setOpenSnackbar(true);
    }
  };

  const columns = [
    {
      field: "id",
      headerName: "Id",
      width: 80,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>{params.id}</Box>
      ),
    },
    {
      field: "value",
      headerName: "Value",
      flex: 1,
      minWidth: 400,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>{params.value}</Box>
      ),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 110,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, height: "100%" }}>
          <Tooltip title="Edit">
            <IconButton
              size="small"
              color="primary"
              onClick={(event) => {
                event.stopPropagation();
                handleOpenEditDialog(params.row);
              }}
            >
              <EditRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Delete">
            <IconButton
              size="small"
              color="error"
              onClick={(event) => {
                event.stopPropagation();
                handleDelete(params.row.id);
              }}
            >
              <DeleteRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      ),
    },
  ];

  return (
    <Box sx={{ width: "100%", maxWidth: "1700px" }}>
      <Paper
        variant="outlined"
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: 2.5,
          borderRadius: 3,
          borderColor: "divider",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 2,
          flexWrap: "wrap",
        }}
      >
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Diet Master
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {rowCount} {rowCount === 1 ? "item" : "items"} in total
          </Typography>
        </Box>

        <Box sx={{ display: "flex", gap: 1.5, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            size="small"
            sx={{ width: { xs: "100%", sm: 320 } }}
            variant="outlined"
            placeholder="Search diet items"
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchRoundedIcon fontSize="small" color="action" />
                </InputAdornment>
              ),
            }}
          />
          <Button
            variant="contained"
            startIcon={<AddRoundedIcon />}
            sx={{ borderRadius: 2, textTransform: "none", fontWeight: 600, whiteSpace: "nowrap" }}
            onClick={handleOpenAddDialog}
          >
            Add Diet
          </Button>
        </Box>
      </Paper>

      {error && (
        <Typography component="h2" variant="h6" color="error" sx={{ mb: 2 }}>
          {error}
        </Typography>
      )}

      <Paper variant="outlined" sx={{ borderRadius: 3, borderColor: "divider", overflow: "hidden" }}>
        <CustomizedDataGrid
          rows={DietPlans}
          columns={columns}
          loading={loading}
          paginationMode="server"
          rowCount={rowCount}
          paginationModel={paginationModel}
          onPaginationModelChange={handlePaginationModelChange}
          pageSizeOptions={[10, 25, 50]}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
          setDietPlanId(null);
        }}
      >
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this diet item?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenDeleteDialog(false);
              setDietPlanId(null);
            }}
            color="primary"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDelete}
            disabled={Deleteloading}
            variant="contained"
            color="error"
            size="small"
          >
            {Deleteloading ? "Deleting ..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Dialog */}
      <Dialog
        open={showAddEditDialog}
        onClose={() => setShowAddEditDialog(false)}
        PaperProps={{ sx: { borderRadius: 3, width: 420, maxWidth: '100%' } }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{dietPlanId ? "Edit Diet Item" : "Add Diet Item"}</DialogTitle>
        <DialogContent>
          <TextField
            label="Value"
            value={dietPlanValue}
            onChange={(e) => setDietPlanValue(e.target.value)}
            fullWidth
            autoFocus
            margin="dense"
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setShowAddEditDialog(false)} sx={{ textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveDietPlan}
            variant="contained"
            sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 600 }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={openSnackbar}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} variant="filled" sx={{ fontWeight: 600 }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
}
